/**
 * Site Footer — single source of truth for venue address, hours, socials,
 * and navigation. Includes embedded Local Business JSON-LD for Google
 * Knowledge Panel and Maps eligibility.
 *
 * Wire by importing on every public page that should be SEO-indexable
 * (Homepage, Blog, BlogPost, EventsList, EventDetail, Privacy, Terms).
 *
 * Customization hooks (search-replace these in one place):
 *   • PHONE_DISPLAY  — public phone number (currently OMITTED on purpose).
 *   • MAPS_URL       — swap to a Google Business Profile share URL when you have one.
 *   • INSTAGRAM_URL / FACEBOOK_URL — replace `#` placeholders.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Mail, ExternalLink } from 'lucide-react';

// Inline brand-icon SVGs (lucide-react has dropped brand glyphs for trademark reasons)
const InstagramIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);
const FacebookIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

// ─── Safety / public-service badges ─────────────────────────────────────────
// Inline SVGs so they ship with the bundle and never 404. Each is a circular
// "prohibition" mark (red ring + diagonal slash) over a relevant pictogram.
const DontDrinkDriveBadge = ({ size = 56 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    role="img"
    aria-label="Don't drink and drive"
    data-testid="safety-badge-dont-drink-drive"
  >
    {/* outer red ring */}
    <circle cx="32" cy="32" r="29" fill="#0A0A0A" stroke="#E53935" strokeWidth="4" />
    {/* car body silhouette */}
    <g fill="#F2F2F2">
      <path d="M14 38h36v6a2 2 0 0 1-2 2h-3a3 3 0 0 1-6 0h-14a3 3 0 0 1-6 0h-3a2 2 0 0 1-2-2v-6z" />
      <path d="M18 28h28l3 8H15l3-8z" opacity="0.95" />
      <circle cx="22" cy="46" r="2.5" fill="#0A0A0A" />
      <circle cx="42" cy="46" r="2.5" fill="#0A0A0A" />
    </g>
    {/* wine glass overlay (slightly behind slash) */}
    <g fill="#F1E2B8" opacity="0.9">
      <path d="M27 14h10l-1.4 7.5a4.5 4.5 0 0 1-3.6 3.6V31h2v2h-6v-2h2v-5.9a4.5 4.5 0 0 1-3.6-3.6L27 14z" />
    </g>
    {/* diagonal slash */}
    <line x1="14" y1="14" x2="50" y2="50" stroke="#E53935" strokeWidth="5" strokeLinecap="round" />
  </svg>
);

const NoDrugsBadge = ({ size = 56 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    role="img"
    aria-label="No drugs"
    data-testid="safety-badge-no-drugs"
  >
    {/* outer red ring */}
    <circle cx="32" cy="32" r="29" fill="#0A0A0A" stroke="#E53935" strokeWidth="4" />
    {/* syringe + capsule pictogram */}
    <g stroke="#F2F2F2" strokeWidth="3" strokeLinecap="round" fill="none">
      {/* syringe body */}
      <line x1="18" y1="46" x2="36" y2="28" />
      <line x1="20" y1="44" x2="24" y2="48" />
      <line x1="34" y1="30" x2="40" y2="36" />
      <line x1="40" y1="36" x2="46" y2="30" />
    </g>
    {/* capsule (two halves) */}
    <g>
      <rect x="34" y="14" width="16" height="9" rx="4.5" fill="#F1E2B8" />
      <rect x="34" y="14" width="8" height="9" rx="4.5" fill="#F2F2F2" />
      <rect x="34" y="14" width="16" height="9" rx="4.5" fill="none" stroke="#0A0A0A" strokeWidth="1.2" />
    </g>
    {/* diagonal slash */}
    <line x1="14" y1="14" x2="50" y2="50" stroke="#E53935" strokeWidth="5" strokeLinecap="round" />
  </svg>
);

const SafetyBadge = ({ Icon, title, subtitle, testId }) => (
  <div
    data-testid={testId}
    className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-sm hover:border-[#D4A843]/40 transition-colors duration-200"
  >
    <Icon />
    <div className="leading-tight">
      <div className="text-[12px] sm:text-[13px] font-semibold tracking-[0.08em] uppercase text-white">
        {title}
      </div>
      <div className="text-[10px] sm:text-[11px] text-[#9A9A92] mt-0.5">{subtitle}</div>
    </div>
  </div>
);

// ─── Venue constants (also used by JSON-LD below) ──────────────────────────
const VENUE = {
  name:        'Scratch Unleashed',
  legalName:   'Charms Hospitality Services',
  street:      '3rd Floor, Bindal Mall',
  locality:    'Sonari',
  region:      'Jharkhand',
  city:        'Jamshedpur',
  postalCode:  '831011',
  country:     'India',
  countryCode: 'IN',
  lat:         22.8059,
  lng:         86.1979,
  hoursLabel:  'Fri – Sun · 6 PM – Late',
  email:       'admin@scratchjsr.com',
  // TODO: replace with Google Business Profile share URL once published
  // (looks like https://maps.app.goo.gl/XXXX — opens directly to the listing).
  mapsUrl:     'https://www.google.com/maps/search/?api=1&query=Scratch+Unleashed+Bindal+Mall+Sonari+Jamshedpur',
  instagramUrl: 'https://www.instagram.com/scratchjsr',
  facebookUrl:  'https://www.facebook.com/Scratchjsr',
};

const fullAddress = `${VENUE.street}, ${VENUE.locality}, ${VENUE.city}, ${VENUE.region} ${VENUE.postalCode}, ${VENUE.country}`;

// ─── LocalBusiness JSON-LD (rendered ONCE on every page where Footer mounts) ─
const LOCAL_BUSINESS_LD = {
  '@context': 'https://schema.org',
  '@type':    ['NightClub', 'Restaurant', 'BarOrPub'],
  name:        VENUE.name,
  legalName:   VENUE.legalName,
  description: "Jamshedpur's premier nightclub and entertainment venue. World-class DJ nights, VIP table service, premium bottle service, and private event hosting.",
  url:         'https://scratchjsr.com',
  logo:        'https://scratchjsr.com/images/scratch-logo.png',
  image:       'https://scratchjsr.com/images/gallery-1.jpg',
  email:       VENUE.email,
  priceRange:  '₹₹₹',
  address: {
    '@type':         'PostalAddress',
    streetAddress:    `${VENUE.street}, ${VENUE.locality}`,
    addressLocality:  VENUE.city,
    addressRegion:    VENUE.region,
    postalCode:       VENUE.postalCode,
    addressCountry:   VENUE.countryCode,
  },
  geo: {
    '@type':    'GeoCoordinates',
    latitude:   VENUE.lat,
    longitude:  VENUE.lng,
  },
  openingHoursSpecification: [
    {
      '@type':      'OpeningHoursSpecification',
      dayOfWeek:    ['Friday', 'Saturday', 'Sunday'],
      opens:        '18:00',
      closes:       '02:00',
    },
  ],
  servesCuisine: ['Indian', 'Continental', 'Asian Fusion'],
  acceptsReservations: true,
  hasMap:    VENUE.mapsUrl,
  sameAs: [
    VENUE.instagramUrl !== '#' ? VENUE.instagramUrl : null,
    VENUE.facebookUrl  !== '#' ? VENUE.facebookUrl  : null,
  ].filter(Boolean),
};

export function VenueJsonLd() {
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(LOCAL_BUSINESS_LD) }}
    />
  );
}

// ─── Public Footer ──────────────────────────────────────────────────────────
export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="bg-[var(--surface-2,#0A0A0A)] border-t border-white/10 text-[#B9B9B9]"
    >
      <VenueJsonLd />

      <div className="max-w-[var(--container,1200px)] mx-auto px-4 sm:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">

          {/* Column 1 — Brand + tagline */}
          <div className="md:col-span-1">
            <div className="font-['EB_Garamond'] text-2xl text-[var(--gold,#D4A843)] mb-3">
              Scratch Unleashed
            </div>
            <p className="text-sm leading-relaxed text-[#9A9A92] max-w-[28ch]">
              Jamshedpur&rsquo;s premium nightclub — where music, mood and memory meet on every weekend.
            </p>
            <div className="flex items-center gap-3 mt-5">
              <a
                href={VENUE.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Scratch Unleashed on Instagram"
                data-testid="footer-instagram-link"
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[var(--gold,#D4A843)]/15 hover:border-[var(--gold,#D4A843)]/30 transition-colors"
              >
                <InstagramIcon width={16} height={16} className="text-[#D4A843]" />
              </a>
              <a
                href={VENUE.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Scratch Unleashed on Facebook"
                data-testid="footer-facebook-link"
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[var(--gold,#D4A843)]/15 hover:border-[var(--gold,#D4A843)]/30 transition-colors"
              >
                <FacebookIcon width={16} height={16} className="text-[#D4A843]" />
              </a>
            </div>
          </div>

          {/* Column 2 — Visit Us (address + map link) */}
          <div>
            <h3 className="text-[11px] tracking-[0.22em] uppercase text-[#9A9A92] mb-4">
              Visit Us
            </h3>
            <div className="flex items-start gap-3 mb-3">
              <MapPin size={16} className="text-[#D4A843] mt-0.5 shrink-0" />
              <address
                className="not-italic text-sm leading-relaxed text-[#E5E5E5]"
                data-testid="footer-address"
              >
                {VENUE.street}<br />
                {VENUE.locality}, {VENUE.city}<br />
                {VENUE.region} {VENUE.postalCode}, {VENUE.country}
              </address>
            </div>
            <a
              href={VENUE.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-directions-link"
              className="inline-flex items-center gap-1.5 text-sm text-[#D4A843] hover:text-[#E8B85A] transition-colors"
            >
              Get directions <ExternalLink size={13} />
            </a>
          </div>

          {/* Column 3 — Hours + Contact */}
          <div>
            <h3 className="text-[11px] tracking-[0.22em] uppercase text-[#9A9A92] mb-4">
              Hours &amp; Contact
            </h3>
            <div className="flex items-start gap-3 mb-3">
              <Clock size={16} className="text-[#D4A843] mt-0.5 shrink-0" />
              <div className="text-sm leading-relaxed text-[#E5E5E5]" data-testid="footer-hours">
                {VENUE.hoursLabel}<br />
                <span className="text-[#9A9A92] text-xs">
                  Weekdays: private events &amp; reservations
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={16} className="text-[#D4A843] mt-0.5 shrink-0" />
              <a
                href={`mailto:${VENUE.email}`}
                data-testid="footer-email-link"
                className="text-sm text-[#E5E5E5] hover:text-[#D4A843] transition-colors break-all"
              >
                {VENUE.email}
              </a>
            </div>
            {/* TODO: when you have a public phone, add it here:
                <div className="flex items-start gap-3 mt-3">
                  <Phone size={16} className="text-[#D4A843] mt-0.5 shrink-0" />
                  <a href="tel:+91XXXXXXXXXX" className="...">+91 XXXXX XXXXX</a>
                </div>
            */}
          </div>

          {/* Column 4 — Explore */}
          <div>
            <h3 className="text-[11px] tracking-[0.22em] uppercase text-[#9A9A92] mb-4">
              Explore
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/"
                  className="text-[#E5E5E5] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-home"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-[#E5E5E5] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-blog"
                >
                  Blog &amp; Guides
                </Link>
              </li>
              <li>
                <Link
                  to="/checkin"
                  className="text-[#E5E5E5] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-checkin"
                >
                  Guest Check-in
                </Link>
              </li>
              <li>
                <Link
                  to="/promoters"
                  className="text-[#E5E5E5] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-promoters"
                >
                  Promoter Program
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-[#E5E5E5] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-about"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-[#E5E5E5] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-contact"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-[#E5E5E5] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-faq"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-[#9A9A92] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-privacy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-and-conditions"
                  className="text-[#9A9A92] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-terms"
                >
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/refund-policy"
                  className="text-[#9A9A92] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-refund"
                >
                  Refund &amp; Cancellation
                </Link>
              </li>
              <li>
                <Link
                  to="/shipping-policy"
                  className="text-[#9A9A92] hover:text-[#D4A843] transition-colors"
                  data-testid="footer-link-shipping"
                >
                  Shipping &amp; Delivery
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Safety / public-service strip */}
        <div
          data-testid="footer-safety-strip"
          className="mt-12 pt-6 border-t border-white/10"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A72]">
              Party Responsibly
            </p>
            <div className="flex flex-wrap gap-3">
              <SafetyBadge
                Icon={DontDrinkDriveBadge}
                title="Don't Drink &amp; Drive"
                subtitle="Use a cab. Stay alive."
                testId="footer-safety-dont-drink-drive"
              />
              <SafetyBadge
                Icon={NoDrugsBadge}
                title="Say No to Drugs"
                subtitle="Strictly prohibited on-premises."
                testId="footer-safety-no-drugs"
              />
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-6 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-3 md:justify-between text-xs text-[#6F6F6F]">
          <div>
            © {year} {VENUE.legalName}. All rights reserved.
          </div>
          <div className="space-x-4">
            <a
              href={VENUE.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#D4A843] transition-colors"
            >
              Find us on Maps
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
